import { Alert, Button, Dialog, Text } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { offline } from '../../lib/api';
import styles from './OfflineStatus.module.css';
import { useOfflineState } from './useOffline';

function time(date: Date | null): string | null {
  return date ? date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : null;
}

/**
 * Says when there is no connection ("Offline – Stand von 14:32") and how many
 * captured entries wait; opens the list of them (ADR 0027).
 */
export function OfflineStatus() {
  const { t } = useTranslation();
  const { online, pending, flush, since } = useOfflineState();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // "Captured offline" is said by the form that saved, in its one toast (#190).
  const failed = pending.filter((p) => p.failure);
  const waiting = pending.length - failed.length;
  if (online && pending.length === 0) return null;

  const label = !online
    ? [t('offline.offline'), since ? t('offline.since', { time: time(since) }) : null]
        .filter(Boolean)
        .join(' – ')
    : failed.length > 0
      ? t('offline.failed', { count: failed.length })
      : t('offline.waiting', { count: waiting });
  const detail = !online && pending.length > 0 ? t('offline.waiting', { count: waiting }) : null;

  return (
    <>
      <button
        type="button"
        aria-live="polite"
        className={styles.pill}
        onClick={() => setOpen(true)}
      >
        <span className={styles.dot} aria-hidden="true" />
        <span>{[label, detail].filter(Boolean).join(' · ')}</span>
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('offline.title')}
        closeLabel={t('common.close')}
        size="sm"
        actions={
          <Button
            loading={busy}
            disabled={!online || waiting === 0}
            onClick={() => {
              setBusy(true);
              void flush().finally(() => setBusy(false));
            }}
          >
            {t('offline.sendNow')}
          </Button>
        }
      >
        {pending.length === 0 ? (
          <Text tone="muted">{t('offline.empty')}</Text>
        ) : (
          <ul className={styles.list}>
            {pending.map((item) => (
              <li key={item.id} className={styles.item}>
                <div>
                  <div>{t(`offline.kind.${item.kind}`)}</div>
                  <Text size="sm" tone="muted">
                    {new Date(item.createdAt).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                  {item.failure && <div className={styles.failure}>{item.failure}</div>}
                </div>
                {item.failure && (
                  <Button variant="plain" size="sm" onClick={() => void offline.discard(item.id)}>
                    {t('offline.discard')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {failed.length > 0 && <Alert tone="warning">{t('offline.failedHint')}</Alert>}
      </Dialog>
    </>
  );
}
